import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { thumbUrl } from "@/lib/utils-app";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CameraIcon as Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

const GRADES = ["Excellent", "Good", "Needs attention"];

function PhotoSlot({ label, value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.storage_path) onChange(r.data.storage_path);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const src = value ? (thumbUrl(value) || value) : null;
  return (
    <div className="flex-1">
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 rounded-lg border border-dashed border-[#E8E0CC] bg-cream overflow-hidden">
        {src ? (
          <div className="relative">
            <img src={src} alt={label} className="w-full h-28 object-cover" />
            <button type="button" onClick={() => onChange("")}
              className="absolute top-1 right-1 bg-black/60 text-white text-[10px] rounded-full px-2 py-0.5">
              Remove
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center h-28 cursor-pointer text-[12px] text-[#6B6B6B] hover:text-ink gap-1" data-testid={testId}>
            <Camera size={20} weight="duotone" />
            {uploading ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
        )}
      </div>
    </div>
  );
}

function GradeRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[13px] font-semibold text-ink w-14">{label}</span>
      <div className="flex gap-1.5 flex-wrap justify-end">
        {GRADES.map((g) => (
          <button key={g} type="button" onClick={() => onChange(value === g ? "" : g)}
            className={`rounded-full px-3 py-1 text-[11px] border transition-colors ${value === g ? "bg-ink text-white border-ink" : "bg-white text-[#3a3a3a] border-[#E5E5E5] hover:border-ink"}`}
            data-testid={`${testId}-${g.toLowerCase().replace(/ /g, "-")}`}>
            {g}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Groomer fills before/after photos + report card + optional rebook window. */
export default function GroomReportDialog({ open, booking, onClose, onSent }) {
  const [form, setForm] = useState({ before_url: "", after_url: "", coat: "", nails: "", ears: "", notes: "", rebook_weeks: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && booking) {
      const r = booking.groom_report || {};
      setForm({
        before_url: r.before_url || "",
        after_url: r.after_url || "",
        coat: r.coat || "",
        nails: r.nails || "",
        ears: r.ears || "",
        notes: r.notes || "",
        rebook_weeks: booking.rebook_weeks || 0,
      });
    }
  }, [open, booking]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/groom-report`, form);
      track("groom_report_sent", { rebook_weeks: form.rebook_weeks || 0 });
      toast.success("Report card sent — your client just got a push 🎉");
      onSent();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="groom-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral">Send report card</DialogTitle>
          <DialogDescription className="text-[12px]">
            Before &amp; after photos, condition grades, and a note — pushed straight to your client.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-3">
            <PhotoSlot label="Before" value={form.before_url} onChange={(v) => setForm((f) => ({ ...f, before_url: v }))} testId="groom-before-upload" />
            <PhotoSlot label="After ✨" value={form.after_url} onChange={(v) => setForm((f) => ({ ...f, after_url: v }))} testId="groom-after-upload" />
          </div>
          <div className="rounded-lg border border-[#EAEAEA] bg-white p-3 space-y-2.5">
            <GradeRow label="Coat" value={form.coat} onChange={(v) => setForm((f) => ({ ...f, coat: v }))} testId="grade-coat" />
            <GradeRow label="Nails" value={form.nails} onChange={(v) => setForm((f) => ({ ...f, nails: v }))} testId="grade-nails" />
            <GradeRow label="Ears" value={form.ears} onChange={(v) => setForm((f) => ({ ...f, ears: v }))} testId="grade-ears" />
          </div>
          <div>
            <Label className="text-xs">Notes for the owner</Label>
            <Textarea rows={2} maxLength={500} value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Fresh trim, teeth look great. Watch the left ear — slight redness."
              data-testid="groom-report-notes" />
          </div>
          <div className="rounded-lg border border-[#E8D9A6] bg-[#FBF7EC]/50 p-3">
            <Label className="text-xs font-semibold">Suggest next groom (sends a reminder)</Label>
            <select
              className="mt-1 w-full rounded-md border border-[#E5E5E5] bg-white px-3 py-2 text-sm"
              value={form.rebook_weeks}
              onChange={(e) => setForm((f) => ({ ...f, rebook_weeks: parseInt(e.target.value, 10) }))}
              data-testid="rebook-weeks-select"
            >
              <option value={0}>No reminder</option>
              {[2, 3, 4, 5, 6, 8, 10, 12].map((w) => (
                <option key={w} value={w}>In {w} weeks</option>
              ))}
            </select>
            <p className="text-[11px] text-[#6B6B6B] mt-1.5">
              Your client gets a one-tap rebook push when it's time — repeat business on autopilot.
            </p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-gold-gradient text-ink font-semibold w-full" data-testid="groom-report-submit">
              {busy ? "Sending…" : "Send report card"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
